import { FC } from 'react';
import { useTranslation } from 'hooks';
import cls from './mentions.module.scss';
import { useCachedSelectedProject } from 'hooks';
import { Icon } from 'shared/ui/icon/icon';
import { ICONS } from 'shared/constants/images';
import { IconTooltip } from 'shared/ui/icon-tooltip/icon-tooltip';
import { getScoreTooltipText } from 'shared/helpers/getTooltip';
import { Card } from 'shared/ui/card/card';
import { track } from 'utils/mixpanel';
import {
  VisibilityQueryMetric,
  VisibilityEntityBase,
} from 'store/api/apiTypes';

interface MentionsProps {
  businessName?: string;
  queries?: VisibilityQueryMetric[];
  selectedCompetitor?: VisibilityEntityBase;
  mentions?: number;
  position?: number;
  competitorDomain?: string;
  productNumber?: number;
}

export const getPositionColor = (position: number, maxScore: number) => {
  if (position === undefined) return cls.red;
  if (maxScore === 10) {
    if (position === 0) return cls.red;
    if (position <= 4) return cls.green;
    if (position <= 8) return cls.orange;
    return cls.red;
  } else {
    if (position === 0) return cls.red;
    if (position <= 8) return cls.green;
    if (position <= 16) return cls.orange;
    return cls.red;
  }
};

export const getQueryColor = (score: number) => {
  if (score >= 4) return cls.green;
  if (score >= 2) return cls.orange;
  return cls.red;
};

export const Mentions: FC<MentionsProps> = (props: MentionsProps) => {
  const { queries, mentions, position, selectedCompetitor, productNumber } =
    props;
  const { t } = useTranslation();
  const { selectedProject, isTrialExpired, isTrial } =
    useCachedSelectedProject();

  const mentionsScore = mentions !== undefined ? mentions : 4;

  const positionScore = selectedCompetitor?.mean_rank ?? position ?? 0;
  const positionScoreColor = getPositionColor(positionScore, 20);

  const querySuccess: number =
    queries?.reduce(
      (acc, query) => (query.total_mentions > 0 ? acc + 1 : acc),
      0
    ) ?? 0;
  const queryAmount = queries?.length;
  const competitorQuerySuccess: number =
    selectedCompetitor?.query_metrics?.reduce(
      (acc, query) => (query.total_mentions > 0 ? acc + 1 : acc),
      0
    ) ?? 0;
  const compareArray = [
    {
      name: selectedCompetitor?.domain || '',
      mentions: selectedCompetitor?.mention_rate || 0,
      position: selectedCompetitor?.mean_rank || 0,
      className: cls.comp,
      querySuccess: competitorQuerySuccess,
    },
    {
      name: selectedProject?.domain || '',
      mentions: mentionsScore,
      position: position ?? 0,
      className: cls.current,
      querySuccess: querySuccess,
    },
  ];

  return (
    <Card>
      <div className={cls.cardWrapper}>
        <div className={cls.card}>
          <div
            className={`${cls.coloredCard} ${positionScoreColor} ${isTrialExpired ? cls.expired : ''}`}
          >
            <div className={cls.cardHeader}>
              {isTrialExpired ? (
                <div className={cls.lockIcon}>
                  <Icon pathToIcon={ICONS.lockGray} size={16} />
                </div>
              ) : (
                <h3>
                  {positionScore === 0 ? '>20' : positionScore}
                  <span>/20</span>
                </h3>
              )}
              <IconTooltip
                id={`tooltip-position`}
                iconSize={18}
                maxWidth={320}
                onMouseEnter={() =>
                  track('AIS:PROD_AVG_POSITION-TOOLTIP-HOVER', {
                    product: productNumber,
                    is_trial: isTrial,
                  })
                }
              >
                <span>{getScoreTooltipText('position')}</span>
              </IconTooltip>
            </div>
            <div>
              <p>{t('averagePosition')}</p>
              <span className={cls.caption}>
                {t('whenMentionedInResponses')}
              </span>
            </div>
          </div>
          {selectedCompetitor !== undefined && (
            <div className={cls.compare}>
              {compareArray.map(item => (
                <div
                  className={`${cls.score} ${item.className}`}
                  key={item.name}
                >
                  <h5>{item.name}</h5>
                  {isTrialExpired ? (
                    <div className={`${cls.lockIcon} ${cls.small}`}>
                      <Icon pathToIcon={ICONS.lockGray} size={14} />
                    </div>
                  ) : (
                    <p className={getPositionColor(item.position, 20)}>
                      {item.position === 0 ? '>20' : item.position}/20
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className={cls.card}>
          <div
            className={`${cls.coloredCard} ${getQueryColor(selectedCompetitor ? competitorQuerySuccess : querySuccess)} ${isTrialExpired ? cls.expired : ''}`}
          >
            <div className={cls.cardHeader}>
              {isTrialExpired ? (
                <div className={cls.lockIcon}>
                  <Icon pathToIcon={ICONS.lockGray} size={16} />
                </div>
              ) : (
                <h3>
                  {selectedCompetitor ? competitorQuerySuccess : querySuccess}/
                  {queryAmount}
                </h3>
              )}
              <IconTooltip
                id={`tooltip-queries`}
                iconSize={18}
                maxWidth={320}
                place='top-start'
                onMouseEnter={() =>
                  track('AIS:PROD_QUERY_SUCCESS-TOOLTIP-HOVER', {
                    product: productNumber,
                    is_trial: isTrial,
                  })
                }
              >
                <span>{getScoreTooltipText('query')}</span>
              </IconTooltip>
            </div>
            <div>
              <p>{t('querySuccess')}</p>
              <span className={cls.caption}>{t('queriesWithMentions')}</span>
            </div>
          </div>
          {selectedCompetitor !== undefined && (
            <div className={cls.compare}>
              {compareArray.map(item => (
                <div
                  key={item.name}
                  className={`${cls.score} ${item.className}`}
                >
                  <h5>{item.name}</h5>
                  {isTrialExpired ? (
                    <div className={`${cls.lockIcon} ${cls.small}`}>
                      <Icon pathToIcon={ICONS.lockGray} size={14} />
                    </div>
                  ) : (
                    <p className={getQueryColor(item.querySuccess)}>
                      {item.querySuccess} /{queryAmount}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
